/**
 * LocationPicker - Refactored with FSM
 * Simplified state management using useReducer
 * Broken into SearchBar, MapView, and ConfirmBar components
 */

import React, { useEffect, useRef, useReducer, useCallback, useState } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import { usePlaces } from '@/hooks/usePlaces';
import { useI18n } from '@/i18n/LanguageContext';
import { geolocationService } from '@/lib/geolocation';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthProvider';
import { upsertRepairShopFromPlace } from '@/services/repairShops';
import type { PlaceData } from '@/types/location';
import { pickerReducer, createInitialState, type PickerState } from './LocationPickerState';
import LocationSearchBar from './LocationSearchBar';
import { MapView } from './MapView';
import { ConfirmBar } from './ConfirmBar';
import debounce from 'lodash.debounce';

interface LocationPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (place: PlaceData) => void;
  initialLocation?: { lat: number; lng: number };
  language?: 'en' | 'lv';
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialLocation,
  language,
}) => {
  const { t, language: i18nLanguage } = useI18n();
  const { toast } = useToast();
  const { user } = useAuth();
  const locale = language || i18nLanguage;
  const { loadPlacesLibraries, createSessionToken, getPlaceDetails, reverseGeocode, getCityBounds, clearSession } = usePlaces({ language: locale });

  // FSM State (centralized - includes center)
  const [state, dispatch] = useReducer(pickerReducer, createInitialState(initialLocation));

  // Refs for performance (avoid UI race conditions during map drag)
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null); // google.maps.Map instance
  const panToProgrammaticRef = useRef<((center: { lat: number; lng: number }) => void) | null>(null); // Programmatic pan method from MapView
  const sessionTokenRef = useRef<any>(null); // google.maps.places.AutocompleteSessionToken
  const reverseGeocodeCacheRef = useRef<Map<string, PlaceData>>(new Map());
  const lastReverseGeocodeCenterRef = useRef<{ lat: number; lng: number } | null>(null);
  const hasInteractedRef = useRef(false);
  const isUpdatingSearchFromDragRef = useRef(false); // Prevent search updates from clearing autocomplete selection
  const searchInputRef = useRef<HTMLInputElement | null>(null); // Reference to search input for dialog close prevention
  const isSelectingPlaceRef = useRef(false); // Track if we're in the middle of selecting a place
  const committedSelectionRef = useRef<PlaceData | null>(null); // Committed selection that should never be wiped
  
  // Derived state (for autocomplete biasing)
  const [userCountry, setUserCountry] = React.useState<string>('lv');
  const [cityBounds, setCityBounds] = React.useState<any>(null); // google.maps.LatLngBounds
  
  // Keyboard detection state
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  
  // Business card position (relative to marker)
  const [cardPosition, setCardPosition] = useState<{ top: number; left: number } | null>(null);
  const cardPositionRef = useRef<{ top: number; left: number } | null>(null);
  const isUpdatingCardPositionRef = useRef(false); // Prevent multiple rapid card position updates
  const isProgrammaticMoveRef = useRef(false); // Track programmatic map moves (from POI selection)

  // Debounced reverse geocode (updates preview AND search input on map drag)
  const debouncedReverseGeocode = useRef(
    debounce(async (lat: number, lng: number) => {
      console.log('[LocationPicker] debouncedReverseGeocode called', { lat, lng, hasCommittedSelection: !!committedSelectionRef.current });
      
      // NEVER reverse geocode if there's a committed selection (business place)
      if (committedSelectionRef.current?.place_id) {
        console.log('[LocationPicker] Skipping reverse geocode - committed business selection exists');
        return;
      }

      // Check cache
      const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
      const cached = reverseGeocodeCacheRef.current.get(cacheKey);
      if (cached?.formatted_address) {
        console.log('[LocationPicker] Using cached reverse geocode:', cached.formatted_address);
        isUpdatingSearchFromDragRef.current = true;
        dispatch({ type: 'SET_PREVIEW_ADDRESS', address: cached.formatted_address });
        // DO NOT update search query - only update preview address
        isUpdatingSearchFromDragRef.current = false;
        return;
      }

      // Check distance (>50m from last call)
      if (lastReverseGeocodeCenterRef.current) {
        const googleMaps = window.google?.maps;
        if (!googleMaps?.geometry) {
          console.log('[LocationPicker] Geometry library not available');
          return;
        }
        const distance = googleMaps.geometry.spherical.computeDistanceBetween(
          new googleMaps.LatLng(lastReverseGeocodeCenterRef.current.lat, lastReverseGeocodeCenterRef.current.lng),
          new googleMaps.LatLng(lat, lng)
        );
        console.log('[LocationPicker] Distance from last call:', distance, 'm');
        if (distance < 50) {
          console.log('[LocationPicker] Distance < 50m, skipping reverse geocode');
          return;
        }
      }

      try {
        console.log('[LocationPicker] Performing reverse geocode...');
        // Use Geocoder directly for reverse geocode (more reliable)
        const googleMaps = window.google?.maps;
        if (!googleMaps?.Geocoder) {
          console.log('[LocationPicker] Geocoder not available, using fallback');
          // Fallback to usePlaces hook
          const placeData = await reverseGeocode(lat, lng, locale);
          if (placeData?.formatted_address) {
            console.log('[LocationPicker] ✅ Reverse geocode (fallback) success:', placeData.formatted_address);
            reverseGeocodeCacheRef.current.set(cacheKey, placeData);
            lastReverseGeocodeCenterRef.current = { lat, lng };
            isUpdatingSearchFromDragRef.current = true;
            dispatch({ type: 'SET_PREVIEW_ADDRESS', address: placeData.formatted_address });
            // DO NOT update search query - only update preview address
            isUpdatingSearchFromDragRef.current = false;
          }
          return;
        }

        const geocoder = new googleMaps.Geocoder();
        geocoder.geocode(
          { location: { lat, lng }, language: locale },
          (results, status) => {
            console.log('[LocationPicker] Geocoder callback:', { status, hasResults: !!results?.length });
            const okStatus = googleMaps.GeocoderStatus?.OK;
            if (okStatus && status === okStatus && results?.[0]) {
              const address = results[0].formatted_address || '';
              console.log('[LocationPicker] ✅ Reverse geocode success:', address);
              reverseGeocodeCacheRef.current.set(cacheKey, {
                formatted_address: address,
                lat,
                lng,
                phones: [],
              });
              lastReverseGeocodeCenterRef.current = { lat, lng };
              isUpdatingSearchFromDragRef.current = true;
              dispatch({ type: 'SET_PREVIEW_ADDRESS', address });
              // DO NOT update search query - only update preview address (from map drag)
              isUpdatingSearchFromDragRef.current = false;
            } else {
              console.warn('[LocationPicker] ❌ Reverse geocode failed:', status);
            }
          }
        );
      } catch (err) {
        console.warn('[LocationPicker] ❌ Reverse geocode error:', err);
      }
    }, 150) // Reduced to 150ms for faster, more responsive feel
  ).current;

  // Load Google Maps on open
  useEffect(() => {
    if (!isOpen) {
      console.log('[LocationPicker] Modal closed, skipping map load');
      return;
    }

    console.log('[LocationPicker] ========== Modal opened, loading Google Maps ==========');
    console.log('[LocationPicker] Initial state:', {
      isOpen,
      locale,
      hasLoadPlacesLibraries: typeof loadPlacesLibraries === 'function',
      hasCreateSessionToken: typeof createSessionToken === 'function',
      currentWindowGoogle: !!window.google,
      currentWindowGoogleMaps: !!window.google?.maps,
    });

    const loadMaps = async () => {
      const loadStartTime = Date.now();
      try {
        console.log('[LocationPicker] Step 1: Calling loadPlacesLibraries...');
        const loadResult = await loadPlacesLibraries();
        console.log('[LocationPicker] Step 1 result:', {
          success: loadResult,
          duration: Date.now() - loadStartTime + 'ms',
        });
        
        if (!loadResult) {
          throw new Error('loadPlacesLibraries returned false');
        }

        console.log('[LocationPicker] Step 2: Waiting for Places library to be available...');
        // Wait for Google Maps Places to be fully loaded
        let retries = 0;
        const maxRetries = 10;
        while (!window.google?.maps?.places && retries < maxRetries) {
          console.log('[LocationPicker] Waiting for Places library...', {
            retry: retries + 1,
            maxRetries,
            hasWindowGoogle: !!window.google,
            hasWindowGoogleMaps: !!window.google?.maps,
            hasWindowGoogleMapsPlaces: !!window.google?.maps?.places,
          });
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }
        
        if (!window.google?.maps?.places) {
          const errorMsg = 'Google Places library not loaded after timeout';
          console.error('[LocationPicker] ❌ Step 2 failed:', {
            error: errorMsg,
            retries,
            maxRetries,
            hasWindowGoogle: !!window.google,
            hasWindowGoogleMaps: !!window.google?.maps,
            hasWindowGoogleMapsPlaces: !!window.google?.maps?.places,
            windowGoogleType: typeof window.google,
          });
          throw new Error(errorMsg);
        }
        
        console.log('[LocationPicker] ✅ Step 2 completed: Places library loaded after', retries, 'retries');
        console.log('[LocationPicker] Available Google Maps APIs:', {
          hasMaps: !!window.google.maps,
          hasPlaces: !!window.google.maps.places,
          hasGeometry: !!window.google.maps.geometry,
          hasMapConstructor: !!window.google.maps.Map,
          hasAutocompleteSessionToken: !!window.google.maps.places.AutocompleteSessionToken,
        });
        
        // Create session token now that Places is ready
        console.log('[LocationPicker] Step 3: Creating session token...');
        const token = createSessionToken();
        sessionTokenRef.current = token;
        console.log('[LocationPicker] ✅ Step 3 completed: Session token created:', {
          hasToken: !!token,
          tokenType: token?.constructor?.name,
        });
        
        console.log('[LocationPicker] ✅ All steps completed successfully in', Date.now() - loadStartTime, 'ms');
      } catch (error) {
        const errorDetails = {
          error: error instanceof Error ? error.message : String(error),
          errorType: error instanceof Error ? error.constructor.name : typeof error,
          errorStack: error instanceof Error ? error.stack : undefined,
          duration: Date.now() - loadStartTime + 'ms',
          hasWindowGoogle: !!window.google,
          hasWindowGoogleMaps: !!window.google?.maps,
          hasWindowGoogleMapsPlaces: !!window.google?.maps?.places,
        };
        console.error('[LocationPicker] ❌ Failed to load Google Maps:', errorDetails);
        toast({
          title: t('locationPicker.error') || 'Error',
          description: t('locationPicker.errorLoadingMap') || 'Failed to load map',
          variant: 'destructive',
        });
      }
    };

    loadMaps();
  }, [isOpen, loadPlacesLibraries, createSessionToken, toast, t, locale]);

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      console.log('[LocationPicker] Resetting state on open');
      dispatch({ type: 'RESET', initialCenter: initialLocation });
      hasInteractedRef.current = false;
      reverseGeocodeCacheRef.current.clear();
      lastReverseGeocodeCenterRef.current = null;

      // Clear committed selection on open
      committedSelectionRef.current = null;
      
      // Get user location for biasing (GPS / last known / IP fallback)
      console.log('[LocationPicker] Getting user location for biasing...');
      geolocationService.getCurrentLocation().then((location) => {
        const center = { lat: location.latitude, lng: location.longitude };
        console.log('[LocationPicker] User location obtained:', {
          lat: center.lat,
          lng: center.lng,
          country: location.country,
          city: location.city,
        });
        dispatch({ type: 'SET_CENTER', center });
        
        // Validate country code - handle "Unknown" gracefully
        let countryCode = 'lv'; // Default to Latvia
        if (location.country && location.country.toLowerCase() !== 'unknown') {
          const normalized = location.country.toLowerCase();
          // Only use if it's a valid 2-letter ISO code
          if (/^[a-z]{2}$/.test(normalized)) {
            countryCode = normalized;
          }
        }
        setUserCountry(countryCode);
        console.log('[LocationPicker] Country set to:', countryCode);
        
        // Ensure locationBias is set for autocomplete
        // This will be passed to SearchBar via state.center
        
        if (location.city && window.google?.maps) {
          console.log('[LocationPicker] Getting city bounds for:', location.city);
          getCityBounds(location.city, location.country || 'lv')
            .then((bounds) => {
              if (bounds) {
                setCityBounds(bounds);
                console.log('[LocationPicker] ✅ City bounds set');
              } else {
                console.log('[LocationPicker] No city bounds returned');
              }
            })
            .catch((error) => {
              // Silently fail - city bounds are optional
              console.warn('[LocationPicker] Failed to get city bounds:', error);
            });
        } else {
          console.log('[LocationPicker] No city or Google Maps not ready, skipping city bounds');
        }
      }).catch((error) => {
        // Fallback: use default center (Riga) if geolocation fails
        console.warn('[LocationPicker] Geolocation failed, using default center:', error);
        const defaultCenter = { lat: 56.9496, lng: 24.1052 };
        dispatch({ type: 'SET_CENTER', center: defaultCenter });
        setUserCountry('lv');
      });
    } else {
      console.log('[LocationPicker] Modal closed, clearing session');
      // Clear session on close
      if (sessionTokenRef.current) {
        clearSession();
        sessionTokenRef.current = null;
        console.log('[LocationPicker] Session cleared');
      }
    }
  }, [isOpen, getCityBounds, clearSession]);

  // Update business card position above marker using projection (debounced to avoid rapid calls)
  const updateCardPosition = useCallback((lat: number, lng: number) => {
    // Prevent multiple rapid calls
    if (isUpdatingCardPositionRef.current) {
      return;
    }

    if (!mapInstanceRef.current || !mapRef.current) {
      console.log('[LocationPicker] Cannot update card position - map not ready');
      return;
    }

    isUpdatingCardPositionRef.current = true;

    const map = mapInstanceRef.current;
    const mapDiv = mapRef.current;
    const projection = map.getProjection();
    
    if (!projection) {
      console.warn('[LocationPicker] Projection not available');
      isUpdatingCardPositionRef.current = false;
      return;
    }

    try {
      const googleMaps = window.google?.maps;
      if (!googleMaps) {
        console.warn('[LocationPicker] Google Maps API not available');
        isUpdatingCardPositionRef.current = false;
        return;
      }

      // Convert lat/lng to pixel coordinates
      const scale = Math.pow(2, map.getZoom() || 14);
      const worldCoordinate = projection.fromLatLngToPoint(new googleMaps.LatLng(lat, lng));
      const pixelCoordinate = new googleMaps.Point(
        worldCoordinate.x * scale,
        worldCoordinate.y * scale
      );

      // Get map bounds to calculate offset
      const bounds = map.getBounds();
      if (!bounds) {
        isUpdatingCardPositionRef.current = false;
        return;
      }

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const topRight = projection.fromLatLngToPoint(ne);
      const bottomLeft = projection.fromLatLngToPoint(sw);

      // Calculate pixel position relative to map container
      const x = ((worldCoordinate.x - bottomLeft.x) / (topRight.x - bottomLeft.x)) * mapDiv.offsetWidth;
      const y = ((worldCoordinate.y - topRight.y) / (bottomLeft.y - topRight.y)) * mapDiv.offsetHeight;

      // Position card above marker (offset by ~30px for marker height + spacing)
      const cardOffset = 30;
      const cardTop = y - cardOffset;
      const cardLeft = x;

      const position = {
        top: Math.max(80, cardTop), // Ensure it's below search bar (top-4 + search bar height)
        left: Math.max(16, Math.min(cardLeft, mapDiv.offsetWidth - 16)), // Keep within bounds
      };

      setCardPosition(position);
      cardPositionRef.current = position;
      console.log('[LocationPicker] ✅ Card position updated:', position);
    } catch (error) {
      console.warn('[LocationPicker] ❌ Failed to calculate card position:', error);
    } finally {
      // Reset flag after a short delay to allow updates after map settles
      setTimeout(() => {
        isUpdatingCardPositionRef.current = false;
      }, 100);
    }
  }, []);

  // Handle place selection from new LocationSearchBar
  const handlePlaceSelected = useCallback((place: {
    place_id: string;
    name?: string | null;
    formatted_address?: string | null;
    lat: number;
    lng: number;
    phones?: string[];
    website?: string | null;
  }) => {
    console.log('[LocationPicker] ========== handlePlaceSelected called ==========');
    console.log('[LocationPicker] Place data:', place);

    // Mark that we're selecting a place (prevents dialog from closing)
    isSelectingPlaceRef.current = true;
    console.log('[LocationPicker] Setting isSelectingPlaceRef to true');

    // Convert to PlaceData format
    const placeData: PlaceData = {
      place_id: place.place_id,
      name: place.name ?? null,
      formatted_address: place.formatted_address ?? null,
      phones: place.phones || [],
      website: place.website ?? null,
      lat: place.lat,
      lng: place.lng,
    };

    // COMMIT the selection - this prevents it from being wiped
    committedSelectionRef.current = placeData;
    console.log('[LocationPicker] ✅ Committed selection set:', placeData.name);

    // Update search query to show place name
    const displayText = placeData.name || placeData.formatted_address || '';
    console.log('[LocationPicker] Updating search query to:', displayText);
    dispatch({ type: 'SET_SEARCH_QUERY', query: displayText });

    // Set business place and update map center
    console.log('[LocationPicker] Setting business place and updating center');
    dispatch({ type: 'SET_BUSINESS_PLACE', place: placeData });
    dispatch({ type: 'SET_CENTER', center: { lat: place.lat, lng: place.lng } });

    // Clear preview address when business is selected
    dispatch({ type: 'SET_PREVIEW_ADDRESS', address: '' });
    
    // Calculate card position above marker (will be updated by useEffect when map is ready)
    if (place.lat && place.lng) {
      setTimeout(() => {
        updateCardPosition(place.lat, place.lng);
      }, 100);
    }

    // Clear the flag after a short delay (allows place selection to complete)
    setTimeout(() => {
      isSelectingPlaceRef.current = false;
      console.log('[LocationPicker] Clearing isSelectingPlaceRef (selection complete)');
    }, 500);

    console.log('[LocationPicker] ✅ Place selection complete');
  }, [updateCardPosition]);

  // Handle place selection from POI click (with retry and fallback)
  const handlePlaceSelect = useCallback(async (placeId: string, lat?: number, lng?: number) => {
    console.log('[LocationPicker] ========== handlePlaceSelect called ==========');
    console.log('[LocationPicker] placeId:', placeId);
    console.log('[LocationPicker] coordinates:', { lat, lng });
    console.log('[LocationPicker] hasSessionToken:', !!sessionTokenRef.current);
    
    if (!placeId) {
      console.warn('[LocationPicker] ❌ No placeId provided');
      return;
    }

    dispatch({ type: 'SET_LOADING', isLoading: true });
    console.log('[LocationPicker] Loading state set to true');

    // Retry logic: try up to 2 times
    let fullDetails: PlaceData | null = null;
    const token = sessionTokenRef.current || undefined;
    
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        console.log(`[LocationPicker] Attempting getPlaceDetails [${attempt + 1}/2]...`);
        fullDetails = await getPlaceDetails(placeId, token);
        console.log('[LocationPicker] getPlaceDetails returned:', {
          hasData: !!fullDetails,
          place_id: fullDetails?.place_id,
          name: fullDetails?.name,
          hasPhones: !!fullDetails?.phones?.length,
        });
        break; // Success - exit retry loop
      } catch (error) {
        console.warn(`[LocationPicker] getPlaceDetails attempt ${attempt + 1} failed:`, error);
        if (attempt === 1) {
          // Last attempt failed - will use fallback
          console.error('[LocationPicker] ❌ All getPlaceDetails attempts failed, using fallback');
        } else {
          // Wait a bit before retry
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
    }

    // Fallback: if Place Details failed, use reverse geocode from coordinates
    if (!fullDetails) {
      console.log('[LocationPicker] Using fallback: reverse geocode from coordinates');
      if (lat !== undefined && lng !== undefined) {
        try {
          const reverseGeocodeResult = await reverseGeocode(lat, lng, locale);
          if (reverseGeocodeResult) {
            // Create fallback PlaceData from reverse geocode
            fullDetails = {
              place_id: placeId,
              name: null, // No name from reverse geocode
              formatted_address: reverseGeocodeResult.formatted_address || null,
              lat: reverseGeocodeResult.lat || lat,
              lng: reverseGeocodeResult.lng || lng,
              phones: [],
              website: null,
            };
            console.log('[LocationPicker] ✅ Fallback place data created from reverse geocode');
          } else {
            // Last resort: use coordinates only
            fullDetails = {
              place_id: placeId,
              name: null,
              formatted_address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              lat,
              lng,
              phones: [],
              website: null,
            };
            console.log('[LocationPicker] ✅ Fallback place data created from coordinates only');
          }
        } catch (reverseError) {
          console.error('[LocationPicker] ❌ Reverse geocode fallback also failed:', reverseError);
          // Last resort: use coordinates only
          fullDetails = {
            place_id: placeId,
            name: null,
            formatted_address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
            lat,
            lng,
            phones: [],
            website: null,
          };
          console.log('[LocationPicker] ✅ Fallback place data created from coordinates (reverse geocode failed)');
        }
      } else {
        // No coordinates available - cannot proceed
        console.error('[LocationPicker] ❌ No coordinates available for fallback');
        dispatch({ type: 'SET_LOADING', isLoading: false });
        return;
      }
    }

    // COMMIT the selection - this prevents it from being wiped (works for both success and fallback)
    committedSelectionRef.current = fullDetails;
    console.log('[LocationPicker] ✅ Committed selection set (POI):', fullDetails.name || fullDetails.formatted_address);

    // Update search query to show place name or address
    const displayText = fullDetails.name || fullDetails.formatted_address || '';
    console.log('[LocationPicker] Updating search query to:', displayText);
    dispatch({ type: 'SET_SEARCH_QUERY', query: displayText });
    
    // Set business place and update map center
    console.log('[LocationPicker] Setting business place and updating center');
    dispatch({ type: 'SET_BUSINESS_PLACE', place: fullDetails });
    
    // Clear preview address when business is selected
    dispatch({ type: 'SET_PREVIEW_ADDRESS', address: '' });
    
    // Update map center to selected place
    if (fullDetails.lat && fullDetails.lng) {
      // Set programmatic move flag BEFORE updating map
      isProgrammaticMoveRef.current = true;
      dispatch({ type: 'SET_CENTER', center: { lat: fullDetails.lat, lng: fullDetails.lng } });
      console.log('[LocationPicker] Map center updated to:', { lat: fullDetails.lat, lng: fullDetails.lng });
      
      // Calculate card position above marker AFTER map has settled (use idle event via useEffect)
      // Don't call updateCardPosition here - let the idle event handler do it
    }
    
    // Always set loading to false (even if fallback was used)
    dispatch({ type: 'SET_LOADING', isLoading: false });
    console.log('[LocationPicker] ✅ Place selection complete');
  }, [getPlaceDetails, reverseGeocode, locale, updateCardPosition]);

  // Update card position when map is idle (after animations complete) - prevents zoom issues
  useEffect(() => {
    if (state.mode === 'business' && state.place?.lat && state.place?.lng && mapInstanceRef.current) {
      const map = mapInstanceRef.current;
      
      // Update position when map becomes idle (after pan/zoom animations complete)
      const idleListener = map.addListener('idle', () => {
        // Only update if not currently updating and not during programmatic move
        if (!isUpdatingCardPositionRef.current && !isProgrammaticMoveRef.current) {
          updateCardPosition(state.place!.lat!, state.place!.lng!);
        }
        // Clear programmatic move flag after map settles
        if (isProgrammaticMoveRef.current) {
          isProgrammaticMoveRef.current = false;
          console.log('[LocationPicker] Programmatic move flag cleared, updating card position');
          // Update card position now that map has settled
          setTimeout(() => {
            updateCardPosition(state.place!.lat!, state.place!.lng!);
          }, 50);
        }
      });

      // Also listen to zoom_changed for immediate visual feedback (but guard against programmatic moves)
      const zoomListener = map.addListener('zoom_changed', () => {
        if (!isProgrammaticMoveRef.current && !isUpdatingCardPositionRef.current) {
          updateCardPosition(state.place!.lat!, state.place!.lng!);
        }
      });

      // Initial position (after a short delay to ensure map is ready)
      setTimeout(() => {
        if (mapInstanceRef.current && state.place?.lat && state.place?.lng) {
          updateCardPosition(state.place.lat, state.place.lng);
        }
      }, 200);

      return () => {
        const googleMaps = window.google?.maps;
        if (googleMaps?.event) {
          googleMaps.event.removeListener(idleListener);
          googleMaps.event.removeListener(zoomListener);
        }
      };
    } else {
      // Clear position when not in business mode
      setCardPosition(null);
      cardPositionRef.current = null;
      isProgrammaticMoveRef.current = false;
    }
  }, [state.mode, state.place?.lat, state.place?.lng, updateCardPosition]);

  // Handle map idle - called when user finishes dragging/zooming
  const handleIdle = useCallback((center: { lat: number; lng: number }) => {
    console.log('[LocationPicker] ========== handleIdle called ==========');
    console.log('[LocationPicker] Idle details:', {
      center,
      hasCommittedSelection: !!committedSelectionRef.current,
      committedSelectionName: committedSelectionRef.current?.name,
    });

    // NEVER update preview or reverse geocode if there's a committed selection
    if (committedSelectionRef.current?.place_id) {
      console.log('[LocationPicker] Idle ignored - committed business selection exists');
      return;
    }

    console.log('[LocationPicker] Processing idle...');
    hasInteractedRef.current = true;
    dispatch({ type: 'SET_CENTER', center }); // Update state center
    console.log('[LocationPicker] State center updated, calling debounced reverse geocode...');
    debouncedReverseGeocode(center.lat, center.lng); // This will update both preview and search query
  }, [debouncedReverseGeocode]);

  // Handle "Use this repair shop" button
  const handleUseBusiness = useCallback(async () => {
    console.log('[LocationPicker] ========== handleUseBusiness called ==========');
    
    // Use committed selection if available, otherwise fall back to state
    const placeToUse = committedSelectionRef.current || state.place;
    
    console.log('[LocationPicker] Business place:', {
      hasPlace: !!placeToUse,
      place_id: placeToUse?.place_id,
      name: placeToUse?.name,
      address: placeToUse?.formatted_address,
      phones: placeToUse?.phones,
      fromCommitted: !!committedSelectionRef.current,
    });

    if (!placeToUse) {
      console.warn('[LocationPicker] ❌ No place selected, cannot use business');
      return;
    }

    // Silent upsert to repair_shops table
    if (user?.id && placeToUse.place_id) {
      console.log('[LocationPicker] Attempting to save repair shop to DB...');
      try {
        await upsertRepairShopFromPlace(user.id, placeToUse);
        console.log('[LocationPicker] ✅ Repair shop saved to DB');
      } catch (error) {
        // Silently fail - table might not exist yet
        console.warn('[LocationPicker] ⚠️ Failed to save repair shop (non-fatal):', error);
      }
    } else {
      console.log('[LocationPicker] Skipping DB save - no user or place_id');
    }

    console.log('[LocationPicker] Calling onSelect with business place...');
    onSelect(placeToUse);
    console.log('[LocationPicker] Closing picker...');
    onClose();
    console.log('[LocationPicker] ✅ handleUseBusiness completed');
  }, [state.place, user, onSelect, onClose]);

  // Handle "Use this address" button
  const handleUseAddress = useCallback(async () => {
    console.log('[LocationPicker] ========== handleUseAddress called ==========');
    console.log('[LocationPicker] Address selection details:', {
      hasCenter: !!state.center,
      center: state.center,
      hasPreviewAddress: !!state.previewAddress,
      previewAddress: state.previewAddress,
      hasPlace: !!state.place,
      currentMode: state.mode,
      hasCommittedSelection: !!committedSelectionRef.current,
    });

    // Clear committed selection when using address-only (user explicitly chose address over business)
    if (committedSelectionRef.current) {
      console.log('[LocationPicker] Clearing committed selection (using address instead)');
      committedSelectionRef.current = null;
    }

    if (!state.center) {
      console.warn('[LocationPicker] ❌ No center, cannot use address');
      return;
    }

    let placeData: PlaceData;

    // If we have a preview address, use it
    if (state.previewAddress) {
      console.log('[LocationPicker] Using cached preview address:', state.previewAddress);
      placeData = {
        formatted_address: state.previewAddress,
        lat: state.center.lat,
        lng: state.center.lng,
        phones: [],
      };
    } else if (state.place && state.place.formatted_address) {
      console.log('[LocationPicker] Using existing place data (address-only, clearing business name)');
      // Use existing place data (address-only, clear business name)
      placeData = {
        ...state.place,
        name: null, // Clear name for address-only
        phones: [],
      };
    } else {
      console.log('[LocationPicker] No preview address, performing reverse geocode...');
      // Reverse geocode current center
      dispatch({ type: 'SET_LOADING', isLoading: true });
      try {
        const rg = await reverseGeocode(state.center.lat, state.center.lng, locale);
        console.log('[LocationPicker] Reverse geocode result:', {
          hasResult: !!rg,
          address: rg?.formatted_address,
        });
        placeData = rg || {
          formatted_address: '',
          lat: state.center.lat,
          lng: state.center.lng,
          phones: [],
        };
      } catch (error) {
        console.warn('[LocationPicker] ❌ Reverse geocode failed, using coordinates:', error);
        placeData = {
          formatted_address: `${state.center.lat.toFixed(6)}, ${state.center.lng.toFixed(6)}`,
          lat: state.center.lat,
          lng: state.center.lng,
          phones: [],
        };
      } finally {
        dispatch({ type: 'SET_LOADING', isLoading: false });
      }
    }

    console.log('[LocationPicker] Final placeData for onSelect:', {
      address: placeData.formatted_address,
      lat: placeData.lat,
      lng: placeData.lng,
      hasName: !!placeData.name,
    });
    console.log('[LocationPicker] Calling onSelect with address data...');
    onSelect(placeData);
    console.log('[LocationPicker] Closing picker...');
    onClose();
    console.log('[LocationPicker] ✅ handleUseAddress completed');
  }, [state.place, state.previewAddress, state.center, reverseGeocode, locale, onSelect, onClose]);

  // Handle locate me - pan map programmatically (uncontrolled)
  const handleLocateMe = useCallback(async () => {
    console.log('[LocationPicker] ========== handleLocateMe called ==========');
    console.log('[LocationPicker] Getting user location...');
    
    try {
      const location = await geolocationService.getCurrentLocation();
      const center = { lat: location.latitude, lng: location.longitude };
      console.log('[LocationPicker] ✅ User location obtained:', {
        lat: center.lat,
        lng: center.lng,
        country: location.country,
        city: location.city,
      });
      
      // Pan map programmatically using MapView's method (sets programmatic flag)
      if (panToProgrammaticRef.current) {
        panToProgrammaticRef.current(center);
        console.log('[LocationPicker] Map panned programmatically to user location');
      } else if (mapInstanceRef.current) {
        // Fallback if method not available yet
        mapInstanceRef.current.panTo(center);
      }
      
      hasInteractedRef.current = true;
      console.log('[LocationPicker] Map panned to user location, will reverse geocode on idle...');
      // Reverse geocode will be triggered by idle handler
    } catch (error) {
      console.error('[LocationPicker] ❌ Failed to get user location:', error);
      toast({
        title: t('locationPicker.error') || 'Error',
        description: t('locationPicker.errorLocation') || 'Failed to get your location',
        variant: 'destructive',
      });
    }
  }, [toast, t]);

  // Keyboard detection using Visual Viewport API + fallback
  useEffect(() => {
    if (!isOpen) {
      setIsKeyboardOpen(false);
      setIsSearchFocused(false);
      return;
    }
    
    const handleViewportChange = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const keyboardHeight = windowHeight - viewportHeight;
        setIsKeyboardOpen(keyboardHeight > 150); // Threshold for keyboard
      } else {
        // Fallback: use input focus state
        setIsKeyboardOpen(isSearchFocused);
      }
    };
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      return () => {
        window.visualViewport?.removeEventListener('resize', handleViewportChange);
      };
    }
  }, [isOpen, isSearchFocused]);
  
  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  
  // Handle clear search
  const handleClearSearch = useCallback(() => {
    console.log('[LocationPicker] Clearing search and selection');
    // Clear committed selection
    committedSelectionRef.current = null;
    // Clear state
    dispatch({ type: 'CLEAR_SELECTION' });
    dispatch({ type: 'SET_SEARCH_QUERY', query: '' });
    // Clear input
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    // Blur input
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  }, []);
  
  // Early return after all hooks
  if (!isOpen) return null;

  // Get search query from state for clear button visibility
  const hasSearchQuery = !!state.searchQuery || (searchInputRef.current?.value?.trim() || '').length > 0;

  return (
    <div 
      className="fixed inset-0 z-50 flex flex-col"
      style={{
        height: '100dvh', // Dynamic viewport height (excludes keyboard on mobile) - fallback to 100vh in CSS if not supported
      }}
      role="dialog"
      aria-modal="true"
    >
      {/* Ensure autocomplete dropdown is above map and clickable */}
      <style>{`
        .pac-container {
          z-index: 9999 !important;
          position: fixed !important;
          pointer-events: auto !important;
        }
        .pac-item {
          cursor: pointer !important;
          padding: 8px 12px !important;
          pointer-events: auto !important;
        }
        .pac-item:hover {
          background-color: #f0f0f0 !important;
        }
        /* Prevent map from capturing clicks on autocomplete */
        .pac-container * {
          pointer-events: auto !important;
        }
        /* Hide Google Maps 3D/Tilt control */
        .gmp-internal-camera-control,
        .gmp-internal-camera-control.gmnoprint,
        .gmp-internal-camera-control.gmnoprint > button,
        .gmp-internal-camera-control.gmnoprint > button:nth-child(1),
        .gmp-internal-camera-control.gmnoprint > button:nth-child(2),
        gmp-internal-camera-control,
        gmp-internal-camera-control.gmnoprint,
        gmp-internal-camera-control.gmnoprint > button {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `}</style>

      {/* Map - full screen, no padding */}
      <div className="absolute inset-0">
        {/* Header overlay on map - absolute positioned */}
        <div className="absolute top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Always visible back button - prominent and thumb-friendly */}
            <button 
              onClick={onClose} 
              className="p-2 -ml-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft size={20} className="text-gray-700" />
            </button>
            {/* Clear button on the right (when search has value) */}
            {hasSearchQuery && (
              <button 
                onClick={handleClearSearch} 
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Clear search"
              >
                <X size={20} className="text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Search bar overlay on map */}
        <div className="absolute top-20 left-4 right-4 z-30">
          <LocationSearchBar
            map={mapInstanceRef.current}
            country={userCountry}
            onPlaceSelected={handlePlaceSelected}
            sessionToken={sessionTokenRef.current}
            disabled={state.isLoading}
            inputRef={searchInputRef}
            searchQuery={state.searchQuery}
            onSearchQueryChange={(query) => dispatch({ type: 'SET_SEARCH_QUERY', query })}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
          />
        </div>
        
        <MapView
          mapRef={mapRef}
          defaultCenter={state.center} // Only used on initial mount (uncontrolled)
          selectedPlace={state.place}
          mode={state.mode}
          onPOIClick={handlePlaceSelect}
          onIdle={handleIdle}
          onLocateMe={handleLocateMe}
          language={locale}
          disabled={state.isLoading}
          onMapReady={(map, panToProgrammatic) => {
            mapInstanceRef.current = map;
            panToProgrammaticRef.current = panToProgrammatic || null;
            console.log('[LocationPicker] Map instance and panToProgrammatic method received from MapView');
          }}
        />

        {/* Business card (if business selected) - positioned above marker */}
        {state.mode === 'business' && state.place?.place_id && state.place?.name && (
          <div 
            className="absolute z-20 bg-white rounded-xl border border-gray-200 shadow-lg p-4 max-w-[calc(100%-2rem)]"
            style={{
              top: cardPosition ? `${cardPosition.top}px` : 'auto',
              left: cardPosition ? `${cardPosition.left}px` : 'auto',
              transform: cardPosition ? 'translateX(-50%)' : 'none',
              display: cardPosition ? 'block' : 'none',
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-gray-900 truncate">{state.place.name}</h3>
                {state.place.formatted_address && (
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">{state.place.formatted_address}</p>
                )}
                {state.place.phones && state.place.phones.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {state.place.phones.map((phone, idx) => (
                      <a
                        key={idx}
                        href={`tel:${phone.replace(/\s+/g, '')}`}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {phone}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  // Clear committed selection and state
                  committedSelectionRef.current = null;
                  setCardPosition(null);
                  cardPositionRef.current = null;
                  dispatch({ type: 'CLEAR_SELECTION' });
                  // Restore preview address if available
                  if (state.previewAddress) {
                    dispatch({ type: 'SET_PREVIEW_ADDRESS', address: state.previewAddress });
                  }
                }}
                className="rounded-full p-1 hover:bg-gray-100 min-w-[32px] min-h-[32px] flex items-center justify-center flex-shrink-0"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
        )}

        {/* Address preview at bottom center (when dragging or has preview) */}
        {state.previewAddress && state.mode !== 'business' && !isKeyboardOpen && (
          <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-20 bg-white rounded-lg border border-gray-200 shadow-lg px-4 py-2 max-w-[90%]">
            <p className="text-sm text-gray-700 text-center truncate">{state.previewAddress}</p>
          </div>
        )}

        {/* Footer CTA - fades out when keyboard opens - absolute positioned over map */}
        <div 
          className={`absolute bottom-8 left-0 right-0 z-20 bg-transparent p-4 transition-opacity duration-200 ${
            isKeyboardOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          style={{
            paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + 0px)`,
          }}
        >
          <ConfirmBar
            mode={state.mode}
            place={state.place}
            previewAddress={state.previewAddress}
            isLoading={state.isLoading}
            onUseBusiness={handleUseBusiness}
            onUseAddress={handleUseAddress}
            disabled={state.isLoading}
          />
        </div>
      </div>
    </div>
  );
};

